import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import {
  DisputeCaseBaseDto,
} from "src/app/shared/models/dispute-resolution/dispute-base-models";
import { NotificationService } from "src/app/shared/services/notification.service";
import {
  DataListParentCallLogIssue,
  DataListParentCallLogIssueAction,
  DataListParentCallLogProcess,
  DataListParentCallLogSchoolLevel,
  DataListParentCallLogSource,
  DataListRelationship,
} from "src/app/shared/models/data-list";
import { DataListService } from "src/app/shared/services/data-list.service";
import { CreateUpdateParentEngagementCallLogDto, ParentEngagementCallLogDto } from "src/app/shared/models/dispute-resolution/parent-engagement-models";
import { forkJoin, Subscription } from "rxjs";
import { AdministrativeUnit } from "src/app/shared/models/organizations/administrative-unit";
import { School } from "src/app/shared/models/organizations/school";
import { PublicAgencyService } from "src/app/shared/services/public-agency.service";

@Component({
  selector: "app-add-edit-call-log",
  templateUrl: "./add-edit-call-log.component.html",
  styleUrls: ["./add-edit-call-log.component.scss"],
  standalone: false,
})
export class AddEditCallLogComponent implements OnInit, OnDestroy {
  form: FormGroup;

  disputeCase: DisputeCaseBaseDto;

  administrativeUnits: AdministrativeUnit[] = [];
  schools: School[] = [];
  loading: boolean = true;

  actionOptions: DataListParentCallLogIssueAction[] = [];
  actionOtherOptionId: string | undefined;
  processOptions: DataListParentCallLogProcess[] = [];
  sourceOptions: DataListParentCallLogSource[] = [];
  sourceOtherOptionId: string | undefined;
  schoolLevelOptions: DataListParentCallLogSchoolLevel[] = [];
  schoolLevelOtherOptionId: string | undefined;
  relationshipOptions: DataListRelationship[] = [];
  relationshipOtherOptionId: string | undefined;
  issueOptions: DataListParentCallLogIssue[] = [];

  callLog: ParentEngagementCallLogDto;

  isEditMode: boolean = false;

  private subscriptions = new Subscription();

  constructor(
    private notificationService: NotificationService,
    private dataListService: DataListService,
    private publicAgencyService: PublicAgencyService,
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<AddEditCallLogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { disputeCase: DisputeCaseBaseDto; processOptions: DataListParentCallLogProcess[]; callLog: ParentEngagementCallLogDto }
  ) {
    this.disputeCase = data.disputeCase || null;
    this.processOptions = data.processOptions || [];
    this.callLog = data.callLog || null;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit() {
    this.loading = true;
    this.isEditMode = this.callLog && this.callLog.id ? true : false;

    this.subscriptions.add(forkJoin({
      administrativeUnits:
        this.publicAgencyService.getAdministrativeUnitsByCurrentSchoolYear(),
      actionOptions: this.dataListService.getParentCallLogIssueActions({
        pageIndex: 0,
        pageSize: 100,
      }),
      sourceOptions: this.dataListService.getParentCallLogSources({
        pageIndex: 0,
        pageSize: 100,
      }),
      schoolLevelOptions: this.dataListService.getParentCallLogSchoolLevels({
        pageIndex: 0,
        pageSize: 100,
      }),
      relationshipOptions: this.dataListService.getRelationships({
        pageIndex: 0,
        pageSize: 100,
      }),
      issueOptions: this.dataListService.getParentCallLogIssues({
        pageIndex: 0,
        pageSize: 100,
      }),
    }).subscribe({
      next: ({ administrativeUnits, actionOptions, sourceOptions, schoolLevelOptions, relationshipOptions, issueOptions }) => {
        this.administrativeUnits = administrativeUnits;

        if (this.isEditMode) {
          this.publicAgencyService
            .getSchoolsByCurrentSchoolYearAndAu(
              this.callLog?.administrativeUnitId
            )
            .subscribe((schools) => {
              this.schools = schools;
            });
        }

        this.actionOptions = actionOptions;
        this.actionOtherOptionId = this.actionOptions.find(a => a.label === "Other")?.id;
        this.sourceOptions = sourceOptions;
        this.sourceOtherOptionId = this.sourceOptions.find(s => s.label === "Other")?.id;
        this.schoolLevelOptions = schoolLevelOptions;
        this.schoolLevelOtherOptionId = this.schoolLevelOptions.find(
          (option) => option.label === "Other"
        )?.id;
        this.relationshipOptions = relationshipOptions.filter(x => x.group === this.disputeCase.disputeCaseType);
        this.relationshipOtherOptionId = this.relationshipOptions.find(
          (option) => option.label === "Other"
        )?.id;
        this.issueOptions = issueOptions;

        this.initForm();
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.notificationService.alert("Error loading data", "Error");
      },
    }));
  }

  initForm() {
    this.form = this.fb.group({
      recordCreated: [{ value: this.callLog?.createdOn ? new Date(this.callLog.createdOn) : new Date(), disabled: true }, Validators.required],
      dateOfContact: [this.callLog?.dateOfContact ? new Date(this.callLog.dateOfContact) : null, Validators.required],
      action1Id: [this.callLog?.action1Id || undefined],
      action1Other: [this.callLog?.action1Other || ""],
      dateClosed: [this.callLog?.dateClosed ? new Date(this.callLog.dateClosed) : null],
      processId: [this.callLog?.processId || undefined, Validators.required],
      sourceId: [this.callLog?.sourceId || undefined],
      administrativeUnitId: [this.callLog?.administrativeUnitId || undefined],
      schoolId: [this.callLog?.schoolId || undefined],
      schoolLevelId: [this.callLog?.schoolLevelId || undefined],
      military: [this.callLog?.military || ""],
      firstName: [this.callLog?.firstName || ""],
      lastName: [this.callLog?.lastName || ""],
      phone: [this.callLog?.phone || ""],
      email: [this.callLog?.email || ""],
      relationshipId: [this.callLog?.relationshipId || undefined],
      relationshipOther: [this.callLog?.relationshipOther || ""],
      issue1Id: [this.callLog?.issue1Id || undefined],
      issue2Id: [this.callLog?.issue2Id || undefined],
      issue3Id: [this.callLog?.issue3Id || undefined],
      notes: [this.callLog?.notes || ""],
      callOrigin: [this.callLog?.callOrigin || ""],
    });
  }

  onAdministrativeUnitChange(auId: string) {
    if (!auId) {
      return;
    }

    this.form.get("schoolId")?.setValue("");
    this.form.get("schoolId")?.disable();

    this.publicAgencyService
      .getSchoolsByCurrentSchoolYearAndAu(auId)
      .subscribe((schools) => {
        this.schools = schools;
        this.form.get("schoolId")?.enable();
      });
  }

  onCancel() {
    this.dialogRef.close();
  }

  onSave() {
    if (this.form.valid) {
      // Check if all form fields are empty before mapping
      const values = this.form.getRawValue();
      const isEmpty = Object.values(values).every(
        (v) => v === undefined || v === null || v === "" || v === false
      );
      if (isEmpty) {
        this.notificationService.error(
          "Cannot save an empty call log. Please fill out at least one field."
        );
        return;
      }
      const callLogData = this.mapCallLogPayloadFromForm();
      this.dialogRef.close(callLogData);
    } else {
      this.form.markAllAsTouched();
      this.notificationService.error("Please fix errors before saving.");
    }
  }

  private mapCallLogPayloadFromForm(): CreateUpdateParentEngagementCallLogDto {

    return {
      id: this.callLog?.id,
      disputeCaseId: this.disputeCase.id,
      dateOfContact: this.form.get("dateOfContact").value,
      processId: this.form.get("processId").value,
      process: this.processOptions.find(p => p.id === this.form.get("processId").value),
      sourceId: this.form.get("sourceId").value,
      source: this.sourceOptions.find(s => s.id === this.form.get("sourceId").value),
      administrativeUnitId: this.form.get("administrativeUnitId").value || undefined,
      administrativeUnit: this.administrativeUnits.find(au => au.id === this.form.get("administrativeUnitId").value),
      schoolId: this.form.get("schoolId").value || undefined,
      school: this.schools.find(s => s.id === this.form.get("schoolId").value),
      schoolLevelId: this.form.get("schoolLevelId").value,
      schoolLevel: this.schoolLevelOptions.find(sl => sl.id === this.form.get("schoolLevelId").value),
      military: this.form.get("military").value,
      firstName: this.form.get("firstName").value,
      lastName: this.form.get("lastName").value,
      phone: this.form.get("phone").value,
      email: this.form.get("email").value,
      relationshipId: this.form.get("relationshipId").value,
      relationship: this.relationshipOptions.find(r => r.id === this.form.get("relationshipId").value),
      issue1Id: this.form.get("issue1Id").value,
      issue1: this.issueOptions.find(i => i.id === this.form.get("issue1Id").value),
      issue2Id: this.form.get("issue2Id").value,
      issue2: this.issueOptions.find(i => i.id === this.form.get("issue2Id").value),
      issue3Id: this.form.get("issue3Id").value,
      issue3: this.issueOptions.find(i => i.id === this.form.get("issue3Id").value),
      action1Id: this.form.get("action1Id").value,
      action1: this.actionOptions.find(a => a.id === this.form.get("action1Id").value),
      action1Other: this.form.get("action1Other").value,
      dateClosed: this.form.get("dateClosed").value,
      notes: this.form.get("notes").value,
      callOrigin: this.form.get("callOrigin").value,
    } as CreateUpdateParentEngagementCallLogDto;
  }
}
